import { CellContext } from '@tanstack/react-table';
import { useEffect, useRef } from 'react';
import { CompanyCustomBenefitAssignmentEmployee } from '@pxtra-client-lib-legacy/gen';
import { CurrencyField } from '../../../atomic/input/currencyField';
import { TooltipComponent } from '../../../atomic/feedback';
import { themeConfig } from '../../../../theme/themeConfig';
import { getAssignmentType, getAssignmentAmount, getMaximumValidAmount } from '../utils';
import { useCustomBenefitTableContext } from '../context';
import Util from '../../../../../misc/util';
import _ from 'lodash';

export const BudgetCell = ({
  row
}: CellContext<CompanyCustomBenefitAssignmentEmployee, unknown>) => {
  const {
    companyAmount,
    assignmentsStart,
    createAssignments,
    focusedEmployeeId,
    setFocusedEmployeeId
  } = useCustomBenefitTableContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const employee = row.original;

  const assignmentType = getAssignmentType(employee);
  const assignmentAmount =
    assignmentType === 'global' ? companyAmount : getAssignmentAmount(employee);
  const maximumValidAmount = getMaximumValidAmount(employee);
  const isInvalidAssignment = [
    'invalid_global_assignment',
    'invalid_individual_assignment'
  ].includes(assignmentType);
  const isDisabled = assignmentType === 'assignment_forbidden';

  useEffect(() => {
    if (focusedEmployeeId === employee.id) {
      const timer = setTimeout(() => inputRef.current?.focus(), 0);
      setFocusedEmployeeId(undefined);
      return () => clearTimeout(timer);
    }
  }, [focusedEmployeeId, employee.id, setFocusedEmployeeId]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  const handleFocus = () => {
    inputRef.current?.select();
  };

  const restrictToMaxAmount = (amount: number) => {
    return maximumValidAmount !== undefined
      ? (_.min([amount, maximumValidAmount]) ?? amount)
      : amount;
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = event.currentTarget.value;
    const newAmount = parseFloat(inputValue.replace('.', '').replace(',', '.'));

    if (_.isNumber(newAmount) && !_.isNaN(newAmount) && employee.id) {
      const currentAmountCents = getAssignmentAmount(employee);
      if (currentAmountCents !== newAmount * 100) {
        createAssignments({
          startAccountingPeriod: assignmentsStart,
          amount: restrictToMaxAmount(Math.round(newAmount * 100)),
          employees: [employee.id]
        });
      }
    }
  };

  const tooltipText =
    maximumValidAmount !== undefined ? `Max. ${Util.formatCurrency(maximumValidAmount)}` : '';

  return (
    <TooltipComponent arrow placement="left" title={tooltipText}>
      <CurrencyField
        ref={inputRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={{
          height: '36px',
          fontWeight: isInvalidAssignment ? '550' : '500',
          backgroundColor: isInvalidAssignment ? themeConfig.colors.error.lightest : 'white',
          color: isInvalidAssignment
            ? themeConfig.colors.error.main
            : themeConfig.colors.primary.main
        }}
        value={assignmentAmount / 100}
        disabled={isDisabled}
      />
    </TooltipComponent>
  );
};
